/* components/patterns/notifications/NotificationsBell.jsx — колокол в топбаре (S11).
   Триггер с бейджем непрочитанных (max 9+). Поповер role=dialog: последние 10
   уведомлений + футер (Прочитать всё / Все уведомления / Настройки рассылки).
   Закрытие: Esc / клик-вне. Данные — из useNotificationsStore (общий с инбоксом,
   фикс №50). На xs — fullscreen bottom-sheet (фикс №44), на md/lg/xl — поповер 360px
   у правого края триггера. ХЕНДОФФ: → frontend/src/components/notifications/NotificationsBell.jsx. */

import React from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '../../ui/Icon.jsx';
import { notifMetaFor as metaFor, useNotificationsStore } from '../../../hooks/useNotificationsStore.jsx';
import { fmt } from '../../../lib/format.js';
import { getOverlayRoot } from '../../../lib/overlay.jsx';
import { RR } from '../../../lib/router.jsx';

  const { useState, useRef, useEffect, useLayoutEffect } = React;
  const { useNavigate } = RR;

  function BellRow({ n, showWorkspace, onClick }) {
    const meta = metaFor(n.type);
    return (
      <button type="button" className={'bell-row' + (!n.is_read ? ' bell-row--unread' : '')} onClick={onClick}>
        <span className="bell-row__dot" aria-hidden="true" />
        <span className={'bell-row__icon bell-row__icon--' + meta.tone}><Icon name={meta.icon} size={16} /></span>
        <span className="bell-row__main">
          <span className="bell-row__title">{n.title}</span>
          {n.body && <span className="bell-row__body">{n.body}</span>}
          <span className="bell-row__meta">
            {showWorkspace && n.workspace_name && <span className="bell-row__ws">{n.workspace_name}</span>}
            <time className="bell-row__time num" dateTime={n.created_at}>{fmt.relativeDay(n.created_at)}</time>
          </span>
        </span>
      </button>
    );
  }

  function NotificationsBell({ bp, showWorkspace }) {
    const navigate = useNavigate();
    const store = useNotificationsStore();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, right: 16 });
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const isMobile = bp === 'xs';

    const place = () => {
      const t = triggerRef.current; if (!t || isMobile) return;
      const r = t.getBoundingClientRect();
      setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    };

    useLayoutEffect(() => { if (open) place(); /* eslint-disable-next-line */ }, [open, bp]);
    useEffect(() => {
      if (!open) return;
      const onDoc = (e) => {
        if (panelRef.current && panelRef.current.contains(e.target)) return;
        if (triggerRef.current && triggerRef.current.contains(e.target)) return;
        setOpen(false);
      };
      const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
      const onScroll = () => place();
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onKey);
      if (!isMobile) { window.addEventListener('resize', onScroll, true); window.addEventListener('scroll', onScroll, true); }
      return () => {
        document.removeEventListener('mousedown', onDoc);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onScroll, true);
        window.removeEventListener('scroll', onScroll, true);
      };
    }, [open, isMobile]);

    const count = store.unreadCount;
    const items = store.notifications.slice(0, 10);

    const openItem = (n) => {
      store.markRead(n.id);
      setOpen(false);
      const dest = n.data && n.data.destination;
      if (dest) navigate(dest);
    };

    const panel = (
      <div className={'bell-popover' + (isMobile ? ' bell-popover--sheet' : '')}
           ref={panelRef} role="dialog" aria-label="Уведомления"
           style={isMobile ? { zIndex: 'var(--z-popover)' } : { top: pos.top, right: pos.right, zIndex: 'var(--z-popover)' }}>
        <div className="bell-popover__head">
          <span className="t-h4">Уведомления</span>
          <button className="bell-popover__close" onClick={() => setOpen(false)} aria-label="Закрыть"><Icon name="close" size={16} /></button>
        </div>

        <div className="bell-popover__list">
          {store.loading && [0, 1, 2].map((i) => (
            <div key={i} className="bell-row bell-row--skeleton">
              <span className="bell-row__icon skeleton-box" />
              <span className="bell-row__main"><span className="skeleton-line" style={{ width: '70%' }} /><span className="skeleton-line" style={{ width: '45%' }} /></span>
            </div>
          ))}
          {!store.loading && items.length === 0 && (
            <div className="bell-empty"><Icon name="notifications" size={24} /><span className="text-dim">Нет уведомлений</span></div>
          )}
          {!store.loading && items.map((n) => (
            <BellRow key={n.id} n={n} showWorkspace={showWorkspace} onClick={() => openItem(n)} />
          ))}
        </div>

        <div className="bell-popover__foot">
          <button className="bell-foot__link" onClick={() => { store.markAllRead(); }}>Прочитать всё</button>
          <div className="bell-foot__right">
            <button className="bell-foot__link" onClick={() => { setOpen(false); navigate('/settings?tab=notifications'); }}>Настройки</button>
            <button className="bell-foot__link bell-foot__link--accent" onClick={() => { setOpen(false); navigate('/notifications'); }}>Все уведомления</button>
          </div>
        </div>
      </div>
    );

    const bellLabel = count > 0 ? `Уведомления, непрочитанных: ${count}` : 'Уведомления, нет непрочитанных';

    return (
      <>
        <button ref={triggerRef} className="bell-trigger" aria-label={bellLabel} aria-expanded={open}
                onClick={() => setOpen((o) => !o)}>
          <Icon name="notifications" size={18} />
          {count > 0 && <span className="bell-badge num" aria-hidden="true">{count > 9 ? '9+' : count}</span>}
          <span className="visually-hidden" aria-live="polite">
            {count > 0 ? `Непрочитанных уведомлений: ${count}` : ''}
          </span>
        </button>
        {open && ReactDOM.createPortal(
          isMobile
            ? <div className="overlay bell-sheet-overlay" style={{ zIndex: 'var(--z-popover)' }}>
                <div className="overlay__backdrop" onClick={() => setOpen(false)} />
                {panel}
              </div>
            : panel,
          getOverlayRoot()
        )}
      </>
    );
  }

export { NotificationsBell };
